import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnChanges, OnDestroy, OnInit, SimpleChanges, inject, input, viewChild } from "@angular/core";
import {CartItem, CartService} from "app/Cart/CartService";
import {CourseItem, QdmService, RankItem, Restaurant, ServiceItem} from "app/Services/qdm.service";
import {PipeNumberFormat} from "../Pipes/PipeNumberFormat";
import {EnvLoader} from "app/Environment/EnvLoader";
import {Subscription} from "rxjs";

import {FormsModule} from "@angular/forms";
import {GestionService} from "app/Services/gestion.service";
import {AdmissionService} from "app/Services/admission.service";
import {WeekMenuDisplayComponent} from "../WeekMenuDisplay/WeekMenuDisplay.component";
import {ModalCourseDetailsComponent} from "../Modal/ModalCourseDetails/ModalCourseDetails.component";
import {ModalService} from "app/Services/modal.service";
import {RestaurantHelper} from "app/Utils/RestaurantHelper";

@Component({
  imports: [PipeNumberFormat, FormsModule],
  template: `
    <div (click)="this.courseInfoModal($event)"
         class="hover:bg-gris-4 cursor-pointer transition fadeIn w-full min-h-[4rem] h-[4rem] overflow-hidden rounded border border-gris-3 flex my-1 relative group">
      <!-- Image du plat (réduite en taille) -->
      <div class="h-full relative" [id]="'cart-image-container-' + item().id">
        <!-- L'image sera gérée par setupImageDisplay() -->
      </div>

      <!-- Contenu (optimisé avec marges réduites) -->
      <div class="h-full flex-1 p-2 flex flex-col justify-between">
        <div class="flex gap-1 items-center">
          <input
            #quantityInput
            [disabled]="this.hideDeleteButton"
            type="number"
            [(ngModel)]="tempQuantity"
            (focus)="this.selectAllText()"
            (change)="updateQuantity()"
            (blur)="updateQuantity()"
            class="disabled:cursor-not-allowed w-8 mr-1 text-center text-xs font-bold fg-vert-1 bg-transparent border border-gris-3 rounded px-1 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            [min]="1"
            [max]="isExtraItem ? null : maxQuantity">
          <p class="text-xs font-semibold line-clamp-2 overflow-hidden max-w-full" [innerText]="item().name"></p>
        </div>
        @if (this.isAcceptPayment && this.item().isExtraItem) {
          <p class="text-xs font-semibold fg-gris-1">
            {{ (((item().displayedPrice || 0)|formatNumber) + ' €') }}
          </p>
        }
      </div>

      <!-- Bouton supprimer (plus petit) -->
      @if (!this.hideDeleteButton) {
        <div class="flex items-center p-1">
          <button
            (click)="this.removeItemFromCart(item())"
            class="w-7 h-7 rounded fg-rose-1 border border-rose-1 hover:bg-rose-1 hover:!text-white transition flex items-center justify-center">
            <svg class="w-6 h-6">
              <use href="/assets/images/icons/icons.svg#trash"></use>
            </svg>
          </button>
        </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "cart-item-row"
})
export class CartItemRowComponent implements OnInit, OnDestroy, OnChanges {
  private cartService = inject(CartService);
  private qdm = inject(QdmService);
  private cdr = inject(ChangeDetectorRef);
  private gestionService = inject(GestionService);
  private admissionService = inject(AdmissionService);
  private modal = inject(ModalService);

  readonly item = input.required<CartItem>();
  readonly currentService = input.required<ServiceItem | undefined>();
  readonly iconsServices = input<{
    [key: number]: {
        url: string;
        bg: string;
    };
}>({});

  readonly quantityInput = viewChild.required<ElementRef<HTMLInputElement>>('quantityInput');

  QDM_URL: string = "";
  restaurant: Restaurant | undefined = undefined;
  sub: Subscription = new Subscription();
  date: any;
  childId: number = -1;
  maxQuantity: number = 999;
  tempQuantity: number = 1; // Valeur temporaire pour l'input

  isAcceptPayment: boolean = false;
  isExtraItem: boolean = false;
  hideDeleteButton: boolean = false;

  private readonly ALT_BACKGROUND_COLORS = [
    '#F8B133', // orange-1
    '#8ABD27', // vert-1
    '#E57373', // rose-1
    '#64B5F6', // bleu-1
    '#FFB74D', // orange-3
    '#81C784', // vert-3
    '#F48FB1', // rose-3
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["iconsServices"] && this.item()) {
      // Refaire l'affichage de l'image depuis le début
      this.setupImageDisplay();
    }
  }

  ngOnInit(): void {
    this.QDM_URL = EnvLoader.load().qdmApiUrl;
    this.tempQuantity = this.item().quantity; // Initialiser la quantité temporaire

    // Vérifier si l'item est un extra
    this.checkIfItemIsExtra();

    // Setup initial de l'image
    this.setupImageDisplay();

    this.sub.add(this.qdm.selectedRestaurant$.subscribe(restaurant => {
      this.restaurant = restaurant;
      this.checkIfItemIsExtra();
      this.cdr.detectChanges();
    }));
    this.sub.add(this.qdm.selectedEnterprise$.subscribe(ent => {
      this.isAcceptPayment = ent?.isAcceptPayment || false;
      this.cdr.detectChanges();
    }));
    this.sub.add(this.qdm.selectedDate$.subscribe(date => {
      this.date = date;
      if (!this.isExtraItem) {
        this.updateMaxQuantity();
      }
      this.cdr.detectChanges();
    }));

    this.sub.add(this.qdm.selectChild$.subscribe(child => {
      this.childId = child?.id || -1;
      if (!this.isExtraItem) {
        this.updateMaxQuantity();
      }
      this.cdr.detectChanges();
    }));
    this.sub.add(this.qdm.selectedAdmission$.subscribe(admission => {
      if (!admission) {
        this.hideDeleteButton = false;
        this.cdr.detectChanges();
        return;
      }

      this.hideDeleteButton = true;
      this.cdr.detectChanges();
    }));
  }

  /**
   * Configure l'affichage de l'image depuis le début
   * Essaie d'abord l'image du cours, puis fallback vers l'icône du service
   */
  private setupImageDisplay(): void {
    const item = this.item();
    const currentService = this.currentService();
    if (!item || !currentService) return;

    const container = document.getElementById(`cart-image-container-${item.id}`);
    if (!container) {
      // Si le container n'existe pas encore, on réessaiera après le render
      setTimeout(() => this.setupImageDisplay(), 0);
      return;
    }

    // Vider le conteneur
    container.innerHTML = '';

    // Créer l'image du cours
    const courseImage = document.createElement('img');
    courseImage.src = `${this.QDM_URL}/upload/course_images/${currentService?.rst_id || 0}/image_${item.id}`;
    courseImage.style.backgroundColor = this.getBackgroundColor();
    courseImage.alt = "Image du plat";
    courseImage.className = "h-full w-[4rem] object-cover bg-white";
    courseImage.id = `cart-image-${item.id}`;

    // Gérer l'erreur de chargement
    courseImage.onerror = () => {
      this.displayIconFallback(container);
    };

    // Ajouter l'image au container
    container.appendChild(courseImage);
  }

  /**
   * Affiche l'icône de fallback en cas d'erreur de chargement de l'image
   */
  private displayIconFallback(container: HTMLElement): void {
    const item = this.item();
    if (!item) return;

    // Vider le conteneur
    container.innerHTML = '';

    const rankId = item.rankId;
    const icon = this.iconsServices()[rankId];

    if (!icon || !icon.bg || !icon.url) {
      // Fallback vers l'image par défaut si les icônes ne sont pas disponibles
      this.displayDefaultImage(container);
      return;
    }

    // Créer un div avec l'image d'icône et le background color
    const iconDiv = document.createElement('div');
    iconDiv.style.backgroundColor = `#${icon.bg}`;
    iconDiv.className = 'h-full w-[4rem] object-cover relative overflow-hidden';
    iconDiv.innerHTML = `
            <img src="${icon.url}" class="w-12 object-cover absolute bottom-0 left-0 -translate-x-2 translate-y-2" alt="Icon" />
        `;

    container.appendChild(iconDiv);
    this.cdr.detectChanges();
  }

  /**
   * Affiche l'image par défaut en dernier recours
   */
  private displayDefaultImage(container: HTMLElement): void {
    const item = this.item();
    if (!item) return;

    const imgElement = document.createElement('img');
    imgElement.src = WeekMenuDisplayComponent.IMAGE_NOT_FOUND_URL || '/assets/images/plats/default_image_menu.webp';
    imgElement.className = 'h-full w-[4rem] object-cover';
    imgElement.alt = item.name;

    container.appendChild(imgElement);
    this.cdr.detectChanges();
  }

  /**
   * Vérifie si l'item est un extra en utilisant les données du service courant
   */
  private checkIfItemIsExtra(): void {
    const currentService = this.currentService();
    if (!this.item() || !currentService) {
      this.isExtraItem = false;
      return;
    }

    // Trouver le rank correspondant à cet item
    const rank = currentService.components.find(c =>
      c.courses.some(course => course.id === this.item().id)
    );

    if (!rank) {
      this.isExtraItem = false;
      return;
    }

    // Trouver le course correspondant à l'ID de l'item
    const course = rank.courses.find(course => course.id === this.item().id);

    if (!course) {
      this.isExtraItem = false;
      return;
    }

    // Vérifier si c'est un extra
    this.isExtraItem = course.is_extra || false;
  }

  updateMaxQuantity() {
    if (!this.currentService() || !this.restaurant || !this.date || this.isExtraItem) return;

    this.sub.add(this.qdm.selectedAdmission$.subscribe(admission => {
      if (!admission) return;

      this.sub.add(this.qdm.groupOrderQuantity$.subscribe(groupOrderQuantity => {
        // Obtenir tous les éléments du panier pour ce service
        const cartItems = this.cartService.getCartFor(
          this.currentService()?.id || 0,
          this.restaurant?.id || 0,
          this.childId,
          this.date
        );

        // Compter les plats complets
        const nbOfVeganFullCourses = cartItems
          .filter(item => item.isVeganFullCourse && item.id !== this.item().id)
          .reduce((acc, item) => acc + item.quantity, 0);

        const nbOfMeatFullCourses = cartItems
          .filter(item => item.isMeatFullCourse && item.id !== this.item().id)
          .reduce((acc, item) => acc + item.quantity, 0);

        // Ajuster la quantité pour le plat actuel s'il est un plat complet
        let extraItemsToConsider = 0;
        const itemValue = this.item();
        if (itemValue.isVeganFullCourse) {
          // Un plat complet légume compte comme deux légumes
          extraItemsToConsider = itemValue.quantity;
        } else if (itemValue.isMeatFullCourse) {
          // Un plat complet viande compte comme un légume fantôme
          extraItemsToConsider = itemValue.quantity;
        }

        // Obtenir la quantité restante sans compter l'item actuel
        const currentService = this.currentService();
        this.maxQuantity = this.admissionService.getRemainingQuantity(
          currentService?.id,
          itemValue.rankId,
          this.restaurant?.id,
          this.childId,
          this.date,
          groupOrderQuantity,
          admission
        );

        // Ajouter la quantité actuelle de l'item pour permettre de conserver la même valeur
        // Cette logique est importante pour que l'utilisateur puisse conserver sa sélection actuelle
        this.maxQuantity += itemValue.quantity;

        // Si l'item est un plat complet, ajuster la quantité maximale
        if (itemValue.isVeganFullCourse || itemValue.isMeatFullCourse) {
          // Trouver le composant légume
          const legumeComponent = admission.admissionParts.find(part => part.isLegume);

          if (legumeComponent && legumeComponent.serviceId !== itemValue.rankId) {
            // Pour les plats complets, on doit également vérifier la disponibilité des légumes
            const legumeRemainingQuantity = this.admissionService.getRemainingQuantity(
              currentService?.id,
              legumeComponent.serviceId,
              this.restaurant?.id,
              this.childId,
              this.date,
              groupOrderQuantity,
              admission
            );

            // Si c'est un plat complet, la quantité maximale est limitée par le légume disponible
            this.maxQuantity = Math.min(this.maxQuantity, legumeRemainingQuantity + extraItemsToConsider);
          }
        }

        this.cdr.detectChanges();
      }));
    }));
  }

  updateQuantity() {
    const currentService = this.currentService();
    if (!currentService || !this.restaurant) return;

    // Mettre à jour la quantité maximale disponible si ce n'est pas un extra
    if (!this.isExtraItem) {
      this.updateMaxQuantity();
    }

    // Valider les contraintes
    if (this.tempQuantity < 1) {
      this.tempQuantity = 1;
    }
    if (!this.isExtraItem && this.tempQuantity > this.maxQuantity) {
      this.tempQuantity = this.maxQuantity;
    }

    // Mettre à jour la quantité dans le service de panier seulement si elle a changé
    const item = this.item();
    if (item.quantity !== this.tempQuantity) {
      // Mettre à jour l'item dans le panier
      this.cartService.setItemQuantity(
        item.id,
        this.tempQuantity,
        currentService.id,
        this.restaurant.id,
        this.childId,
        this.date
      );

      // Mettre à jour la valeur locale
      item.quantity = this.tempQuantity;
    }

    this.cdr.detectChanges();
  }

  removeItemFromCart(arg0: CartItem) {
    const currentService = this.currentService();
    if (!currentService || !this.restaurant) return;
    this.cartService.removeItemFromCart(this.item().id, currentService.id, this.restaurant.id, this.childId, this.date);
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  selectAllText() {
    const quantityInput = this.quantityInput();
    if (quantityInput) {
      quantityInput.nativeElement.select();
    }
  }

  getBackgroundColor(): string {
    const item = this.item();
    const currentService = this.currentService();
    if (!item || !currentService) return 'transparent';

    const icon = this.iconsServices()[item.rankId];

    return (
      (icon?.bg ? `#${icon.bg}` : undefined) ||
      this.ALT_BACKGROUND_COLORS[
      currentService.id % this.ALT_BACKGROUND_COLORS.length
        ]
    );
  }

  courseInfoModal(event: MouseEvent) {
    const currentService = this.currentService();
    if (!currentService || !this.restaurant) return;

    if ((event.target as HTMLElement).closest('input') || (event.target as HTMLElement).closest('button')) {
      return;
    }

    let restaurantHelper = new RestaurantHelper(this.restaurant);
    this.modal.open(
      ModalCourseDetailsComponent,
      {
        course: this.findCourse(this.item().id),
        service: currentService,
        restaurantId: currentService?.rst_id || 0,
        childId: this.childId,
        date: this.date,
        rank: this.findRank(this.item().id),
        restaurantHelper: restaurantHelper,
        mode: "day",
        iconsServices: this.iconsServices()
      },
      () => {
      }
    );
  }

  findCourse(courseId: number): CourseItem | undefined {
    const currentService = this.currentService();
    if (!currentService) return undefined;

    for (let component of currentService.components) {
      const course = component.courses.find(c => c.id === courseId);
      if (course) return course;
    }

    return undefined;
  }

  findRank(courseId: number): RankItem | undefined {
    const currentService = this.currentService();
    if (!currentService) return undefined;
    return currentService.components.find(c => c.courses.some(course => course.id === courseId));
  }
}
